"use client";

import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import Aurora from "@/components/bits/Aurora/Aurora";

interface AuroraBackgroundProps {
  colorStops?: string[];
  amplitude?: number;
  blend?: number;
  speed?: number;
  opacity?: number;
  lightOpacity?: number;
}

export default function AuroraBackground({
  colorStops,
  amplitude = 0.6,
  blend = 0.4,
  speed = 0.3,
  opacity = 20,
  lightOpacity = 6,
}: AuroraBackgroundProps) {
  const { resolvedTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  // Only render after component has mounted on the client
  useEffect(() => {
    setMounted(true);
  }, []);

  // Prevent hydration mismatch by not rendering anything until mounted
  if (!mounted) {
    return null;
  }

  if (!resolvedTheme) {
    return null;
  }

  const isDark = resolvedTheme === "dark";
  const effectiveOpacity = isDark ? opacity : lightOpacity;
  const effectiveStops =
    colorStops ??
    (isDark
      ? ["#ea580c", "#f59e0b", "#d97706"]
      : ["#ff5a1f", "#ff7a00", "#ff9f1a"]);
  const effectiveAmplitude = isDark ? amplitude : 0.4;
  const effectiveBlend = isDark ? blend : 0.25;

  if (effectiveOpacity <= 0) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 pointer-events-none z-0"
      style={{
        opacity: effectiveOpacity / 100,
        filter: isDark
          ? "none"
          : "saturate(1.25) contrast(1.08) brightness(1.02)",
        maskImage: isDark
          ? undefined
          : "radial-gradient(90% 65% at 50% 0%, black 0%, rgba(0,0,0,0.75) 45%, transparent 100%)",
        WebkitMaskImage: isDark
          ? undefined
          : "radial-gradient(90% 65% at 50% 0%, black 0%, rgba(0,0,0,0.75) 45%, transparent 100%)",
      }}
    >
      <Aurora
        colorStops={effectiveStops}
        amplitude={effectiveAmplitude}
        blend={effectiveBlend}
        speed={speed}
      />
    </div>
  );
}
